import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNotify } from 'react-admin';
import { schoolApi } from '../../common/schoolApi';

/**
 * Fill the paper from the test's blueprint, in one click.
 *
 * The blueprint is the exam, the subjects, the difficulty and the number of
 * questions the teacher saved on the test itself -- the same four answers a
 * student gives in the custom-quiz screen. This panel does not ask for them
 * again: if they are wrong, the fix is to change the test, not to type
 * something different here that the test will not remember.
 *
 * WHAT IT PRODUCES IS A DRAFT PAPER. Questions arrive as ordinary rows and
 * the panel below is fully editable: remove the weak ones, add your own from
 * the bank, change the marks. Nothing is marked ready by generating.
 *
 * Only shown when the paper can still change -- an assigned test is frozen,
 * and the screen offers "duplicate" instead.
 */

export const GeneratePanel = ({ test, onChange }: { test: any; onChange: () => void }) => {
    const notify = useNotify();
    const [busy, setBusy] = useState(false);
    // The blueprint's count is the default, but a teacher topping up a paper
    // usually wants a few more, not another twenty.
    const [count, setCount] = useState<string>(String(test.target_question_count || 20));
    const [marks, setMarks] = useState('4');
    const [negative, setNegative] = useState('1');
    const [replace, setReplace] = useState(false);

    const has = (test.question_count || 0) > 0;

    const generate = async () => {
        if (replace && !window.confirm(`Remove all ${test.question_count} questions and draw a new paper?`)) return;
        setBusy(true);
        try {
            const r = await schoolApi.post(`school-test/generate/${test.id}`, {
                count: Number(count),
                marks: Number(marks),
                negative_marks: Number(negative),
                replace,
            });
            // The API's own sentence says how many landed and whether the bank
            // came up short; it is written for a teacher, so it is shown as is.
            notify(r.message, { type: 'success', autoHideDuration: 8000 });
            setReplace(false);
            onChange();
        } catch (e: any) {
            notify(e.message, { type: 'error', autoHideDuration: 10000 });
        } finally {
            setBusy(false);
        }
    };

    const subjects: number[] = test.subject_ids || [];
    const blueprint = [
        subjects.length ? `${subjects.length} subject${subjects.length === 1 ? '' : 's'}` : test.subject_name || 'every subject',
        test.difficulty ? { Elementary: 'Easy', Moderate: 'Medium', Advance: 'Hard' }[test.difficulty] || test.difficulty : 'any difficulty',
        test.language === 'hi' ? 'Hindi' : 'English',
    ].join(' · ');

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Generate questions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    From the WiZi bank, using this test's settings: {blueprint}. You can remove any of them
                    afterwards, or add your own from the bank below.
                </Typography>

                {!subjects.length && !test.subject_id && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        No subjects are set on this test, so questions will be drawn from every subject. Pick
                        the subjects above and save to narrow it.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        label="How many"
                        type="number"
                        size="small"
                        value={count}
                        onChange={e => setCount(e.target.value)}
                        inputProps={{ min: 1, max: 200 }}
                        sx={{ width: 130 }}
                    />
                    <TextField
                        label="Marks each"
                        type="number"
                        size="small"
                        value={marks}
                        onChange={e => setMarks(e.target.value)}
                        inputProps={{ min: 1, max: 100 }}
                        sx={{ width: 130 }}
                    />
                    <TextField
                        label="Negative marks"
                        type="number"
                        size="small"
                        value={negative}
                        onChange={e => setNegative(e.target.value)}
                        inputProps={{ min: 0, max: 99, step: 0.5 }}
                        sx={{ width: 150 }}
                        helperText="0 for none"
                    />
                    {has && (
                        <FormControlLabel
                            control={<Checkbox checked={replace} onChange={e => setReplace(e.target.checked)} />}
                            label="Replace the current questions"
                        />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="contained"
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                        disabled={busy || !count || Number(count) < 1}
                        onClick={generate}
                    >
                        {has && !replace ? 'Add more' : 'Generate'}
                    </Button>
                </Box>

                {has && !replace && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Questions already on the paper are never drawn twice.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};
